
import { renderToStaticMarkup } from "react-dom/server";
import { buildVehicleScanPayload } from "../utils/vehicleScan";
import PartLabelTemplate, { type PartLabelTemplateProps } from "../components/stock/PartLabelTemplate";
import { defaultPartLabelDisplay, type PartLabelDisplay } from "../models/LabelDisplay";

import WarehouseLabelTemplate from "../components/stock/WarehouseLabelTemplate";
import {
  defaultWarehouseLabelDisplay,
  type WarehouseLabelDisplay
} from "../models/WarehouseLabelDisplay";
import VehicleLabelTemplate from "../components/vehicles/VehicleLabelTemplate";
import {
  defaultVehicleLabelDisplay,
  type VehicleLabelDisplay
} from "../models/VehicleLabelDisplay";

import { useQueryClient } from "@tanstack/react-query";
import { IWarehouseLocation } from "../models/Location";
import { IPartBin } from "../models/PartBin";
import { IPart } from '../models/Part';
import { IAccount } from "../models/account/IAccount";
import useCustomerDetails from "../data/useCustomerDetails";
import {
  partLabelDisplayQueryKey,
  partLabelDisplayStaleTimeMs,
  partLabelDisplayUrl,
} from "../data/usePartLabels";
import {
  warehouseLabelDisplayQueryKey,
  warehouseLabelDisplayStaleTimeMs,
} from "../data/useWarehouseLabels";
import {
  vehicleLabelDisplayQueryKey,
  vehicleLabelDisplayStaleTimeMs,
} from "../data/useVehicleLabels";
import React from "react";
import useApiHelper from "./useApiHelper";
import { DefaultResponseWithData } from "../models/system";
import PartLabelCoreTemplate from "../components/stock/PartLabelCoreTemplate";
import { IVehicle } from "../models/Vehicle";
import { IPartProTradeBidVehicleDetail } from "../models/partPro/IPartProTradeBids";
import { isPartProTradeAward, partLabelSecondLogo } from "../utils/partLabelLogo";
import PriceFormat from "../utils/priceFormat";
import i18n from "../i18n";

const toDataURL = async (url: string) => {
  const res = await fetch(url);
  const blob = await res.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  });
}

export function useLabelGenerator() {
  const queryClient = useQueryClient();
  const { brand } = useCustomerDetails();
  const { get } = useApiHelper();

  const generatePartLabel = async (
    part: IPart
  ) => {
    // The API resolves the part tag assigned to the part's category, else the Default tag.
    const displayData = await queryClient.fetchQuery({
      queryKey: partLabelDisplayQueryKey(part.categoryId),
      queryFn: () =>
        get<DefaultResponseWithData<PartLabelDisplay>>(partLabelDisplayUrl(part.categoryId)),
      staleTime: partLabelDisplayStaleTimeMs,
    });

    const display = displayData.data ?? defaultPartLabelDisplay;

    const qrDataUrl = await toDataURL(
      `https://api.qrserver.com/v1/create-qr-code/?size=500x500&data=${part.id}`
    );

    const priceNumber = part.price ?? part.enteredPrice;
    const price = PriceFormat(priceNumber ?? 0);

    // Resolve the assigned account's name so it can show on the label.
    let accountName: string | undefined;
    if (part.accountId) {
      try {
        const accountData = await queryClient.fetchQuery({
          queryKey: ["accounts", part.accountId],
          queryFn: () =>
            get<DefaultResponseWithData<IAccount>>(`/account/${part.accountId}`),
          staleTime: 1000 * 60 * 10,
        });
        accountName = accountData.data?.name;
      } catch {
        // Account lookup failed — print the label without the account name.
      }
    }

    // Only vehicles that went out for trade bidding can have PartPro Trade parts, so every
    // other label skips the lookup. Cached per vehicle so a batch of its parts asks once.
    let isPartProTrade = false;
    const tradeVehicleId = part.vehicle?.partProTradeVehicleId;
    if (tradeVehicleId) {
      try {
        const tradeData = await queryClient.fetchQuery({
          queryKey: ["partpro", "trade", "bids", "vehicle", tradeVehicleId],
          queryFn: () =>
            get<DefaultResponseWithData<IPartProTradeBidVehicleDetail>>(
              `/partpro/trade/vehicles/${tradeVehicleId}`
            ),
          staleTime: 1000 * 60 * 5,
        });
        isPartProTrade = isPartProTradeAward(part.categoryId, tradeData.data);
      } catch {
        // PartPro unreachable or trade off - print the usual label rather than fail.
      }
    }
    const secondLogo = partLabelSecondLogo({ isCore: !!part.isCore, isPartProTrade });

    const html =
    "<!doctype html>" +
    renderToStaticMarkup(
      React.createElement(part.isCore ? PartLabelCoreTemplate : PartLabelTemplate, {
        display,
        logoSrc: brand?.data?.data.logo ?? "",
        secondLogoSrc: secondLogo?.src,
        secondLogoAlt: secondLogo?.alt,
        qrSrc: qrDataUrl as string,
        tagNo: part.tagNumber ?? i18n.t("No Tag No", { ns: "stock" }),
        date: part.created
          ? new Date(part.created).toLocaleDateString().replaceAll("/", " / ")
          : "",
        make: part.vehicle?.combinedMake ?? "",
        model: part.vehicle?.combinedModel ?? "",
        category: part.category?.name ?? "",
        stockNumber: part.stockNumber ?? part.vehicle?.stockNumber ?? "",
        accountName,
        vrm: part.vehicle?.vrm ?? "",
        year: part.vehicle?.dvlaYearOfManufacture?.toString?.() ?? "",
        engineCode: part.vehicle?.engineModelCode ?? "",
        // Fall back to the combined VIN the same way the vehicle label does: vinVerified is only
        // set once someone has confirmed it off the vehicle, and a label with a blank VIN line is
        // worse than one showing the VIN we do hold.
        vin: part.vehicle?.vinVerified || part.vehicle?.combinedVIN || "",
        fuelType: part.vehicle?.fuelType ?? "",
        price,
        position: part.warehouseLocation?.displayName ?? "-",
        qc: part.passedQcOn != null ? i18n.t("QC", { ns: "stock", context: "label" }) : "-",
        imaged: part.images?.length > 0 ? i18n.t("Img", { ns: "stock" }) : "-"
      })
    );

    return html;
  };

  const generateWarehouseLabel = async (
    warehouse: IWarehouseLocation
  ) => {
    const displayData = await queryClient.fetchQuery({
      queryKey: warehouseLabelDisplayQueryKey,
      queryFn: () =>
        get<DefaultResponseWithData<WarehouseLabelDisplay>>(
          `/template/warehouse/label/display`
        ),
      staleTime: warehouseLabelDisplayStaleTimeMs,
    });

    const display = displayData.data ?? defaultWarehouseLabelDisplay;

    const qrDataUrl = await toDataURL(
      `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${warehouse.id}`
    );

    const title = warehouse.displayName ?? i18n.t("No Display Name", { ns: "stock" });

    const html =
      "<!doctype html>" +
      renderToStaticMarkup(
        React.createElement(WarehouseLabelTemplate, {
          display,
          qrSrc: qrDataUrl as string,
          title,
          subtitle: "" // or warehouse.something later
        })
      );

    return html;
  };

  // Part bins print the same 10x5cm QR-and-name label as a warehouse location, but on fixed display
  // settings rather than the warehouse ones — a bin label is only ever the code and the name, so
  // there's nothing for an operator to configure and nothing to inherit from the warehouse label.
  const generatePartBinLabel = async (
    partBin: IPartBin
  ) => {
    // "bin|{id}|{name}" mirrors the vehicle label's "v|{id}|{vrm}" - the prefix is what lets the
    // scanner tell a bin apart from a bare-GUID part label, and the name is there so a scan can be
    // acted on without waiting for a lookup.
    const qrDataUrl = await toDataURL(
      `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=bin|${partBin.id}|${partBin.name ?? "NULL"}`
    );

    const html =
      "<!doctype html>" +
      renderToStaticMarkup(
        React.createElement(WarehouseLabelTemplate, {
          display: defaultWarehouseLabelDisplay,
          qrSrc: qrDataUrl as string,
          title: partBin.name ?? i18n.t("No Name", { ns: "stock" }),
          subtitle: ""
        })
      );

    return html;
  };

  const generateVehicleLabel = async (
    vehicle: IVehicle
  ) => {
    const displayData = await queryClient.fetchQuery({
      queryKey: vehicleLabelDisplayQueryKey,
      queryFn: () =>
        get<DefaultResponseWithData<VehicleLabelDisplay>>(`/template/vehicle/label/display`),
      staleTime: vehicleLabelDisplayStaleTimeMs,
    });

    const display = displayData.data ?? defaultVehicleLabelDisplay;

    const qrDataUrl = display.qr
      ? await toDataURL(
        `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${buildVehicleScanPayload(vehicle)}`
      )
      : undefined;

    const makeModel = `${vehicle.combinedMake ?? ""} ${vehicle.combinedModel ?? ""}`.trim();
    const mileage =
      vehicle.odometerInMiles != null ? `${vehicle.odometerInMiles.toLocaleString()} mi` : "-";

    const html =
      "<!doctype html>" +
      renderToStaticMarkup(
        React.createElement(VehicleLabelTemplate, {
          display,
          logoSrc: brand?.data?.data.logo ?? "",
          qrSrc: qrDataUrl as string | undefined,
          vrm: vehicle.vrm ?? "-",
          makeModel: makeModel || "-",
          stockNumber: vehicle.stockNumber ?? "-",
          vin: vehicle.vinVerified || vehicle.combinedVIN || "-",
          engineCode: vehicle.engineModelCode || "-",
          fuelType: vehicle.fuelType || "-",
          colour: vehicle.colourCurrent || "-",
          mileage,
        })
      );

    return html;
  };


  return { generatePartLabel, generateWarehouseLabel, generatePartBinLabel, generateVehicleLabel };
}